import { useRef, useEffect, useMemo, useState, createElement, useLayoutEffect, useCallback } from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { smoothScrollTo, DateInputUtils, isMobileBrowser, isEmptyInput } from 'api-sdk';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import * as Sentry from '@sentry/react';

var ActivationMode;
(function (ActivationMode) {
    ActivationMode["MOUSE"] = "MOUSE";
    ActivationMode["KEYBOARD"] = "KEYBOARD";
})(ActivationMode || (ActivationMode = {}));
/** Generalization of onClick, allowing keyboard input using Tab and Enter keys */
function onActivate(activate, tabIndex) {
    const safeTabIndex = tabIndex !== null && tabIndex !== void 0 ? tabIndex : (activate ? 0 : -1);
    return activate ? {
        tabIndex: safeTabIndex,
        onClick: (e => activate(e, ActivationMode.MOUSE)),
        onKeyDown: (e => {
            if (e.key == "Enter" && !e.ctrlKey && !e.altKey && !e.metaKey) {
                e.stopPropagation();
                e.preventDefault();
                activate(e, ActivationMode.KEYBOARD);
            }
        })
    } : {
        tabIndex: safeTabIndex
    };
}

// See https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
function usePrevious(val) {
    const ref = useRef();
    useEffect(() => {
        ref.current = val;
    });
    return ref.current;
}
function scrollContainerToChild(container, idx, childrenAccessor) {
    if (container != null && idx != undefined) {
        const children = childrenAccessor ? childrenAccessor(container) : container.children;
        const target = children[idx];
        if (target) {
            const targetTop = Array.from(children).filter((_, i) => i < idx).map(c => c.clientHeight).reduce((a, b) => a + b, 0);
            if (targetTop < container.scrollTop) { // Scroll up
                smoothScrollTo(container, targetTop, 80);
            }
            else if ((targetTop + target.clientHeight) > (container.scrollTop + container.clientHeight)) { // Scroll down
                smoothScrollTo(container, targetTop - (container.clientHeight - target.clientHeight), 80);
            }
        }
    }
}
function useAutoFocus(element, enabled = true) {
    useEffect(() => {
        if (document.activeElement != undefined && document.activeElement.tagName != "BODY" && enabled) {
            element === null || element === void 0 ? void 0 : element.focus();
        }
    }, [element, enabled]);
}
const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "svg"];

var IconName;
(function (IconName) {
    IconName["PLAY"] = "play";
    IconName["DOWNLOAD"] = "download";
    IconName["FILE"] = "file";
    IconName["CHEVRON_LEFT"] = "chevron-left";
    IconName["CHEVRON_RIGHT"] = "chevron-right";
    IconName["EDIT"] = "edit";
    IconName["DROPDOWN_ARROW"] = "dropdown-arrow";
    IconName["CLEAR"] = "clear";
})(IconName || (IconName = {}));
function Icon(props) {
    var _a;
    const className = "cdc-icon "
        + ("icon-" + ((_a = props.iconName) === null || _a === void 0 ? void 0 : _a.toLowerCase())) + " "
        + (props.className || "");
    return (jsx("i", { style: props.style, className: className, onClick: props.onClick, tabIndex: props.tabIndex, onKeyDown: props.onKeyDown }));
}

function RichText(props) {
    return (jsx("div", Object.assign({ dangerouslySetInnerHTML: { __html: props.text } }, (Object.assign(Object.assign({}, props), { text: undefined })))));
}

function MediaDisplay(props) {
    // Obtain URL once, and don't update it even when fileUrlProvider changes,
    // because that could cause re-loading and flickering.
    // (the fileUrlProvider is expected to change with every step)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const url = useMemo(() => props.fileUrlProvider(props.media.id), [props.media.id]);
    const className = "cdc-media-display " + (props.className || "");
    const activate = () => {
        if (props.onMediaClick && url) {
            props.onMediaClick(url);
        }
    };
    let innerContent;
    if (!url) {
        // is not ready yet
        innerContent = null;
    }
    else {
        innerContent = (jsx(MediaDisplayWithUrl, Object.assign({}, props, { url: url })));
    }
    return (jsx("div", Object.assign({}, onActivate(activate), { style: props.style, className: className, tabIndex: props.tabIndex, children: innerContent })));
}
function MediaDisplayWithUrl(props) {
    let content;
    let caption = null;
    const url = props.url;
    const isImageType = IMAGE_EXTENSIONS.includes(props.media.extension);
    if (isImageType) {
        content = (jsx("img", { draggable: "false", src: url, "aria-label": props.caption || "Image" /* TODO i18n */, alt: props.caption || "Image" /* TODO i18n */ }));
    }
    else {
        content = (jsxs("div", { className: "document-container", children: [jsx(Icon, { iconName: IconName.FILE }), jsx("a", { href: url, className: "download-button", children: jsx(RichText, { text: props.downloadLabel || "Download" }) })] }));
    }
    // Render caption
    if (props.caption) {
        caption = (jsx("div", { className: "caption", children: jsx(RichText, { text: props.caption }) }));
    }
    return (jsxs(Fragment, { children: [content, caption] }));
}

function Dropdown(props) {
    const btnRef = useRef(undefined);
    const options = props.options;
    const selectedOption = options.find(opt => opt.id == props.value);
    useAutoFocus(btnRef.current, props.autoFocus);
    return (jsx("div", Object.assign({ ref: btnRef, className: "cdc-dropdown-button " + (props.className || ""), style: props.style }, onActivate(e => createDropdown(Object.assign(Object.assign({}, props), { anchor: e.currentTarget }))), { children: jsx(DropdownElement, { option: selectedOption, onClear: props.onClear, showDropdownArrow: true, fileUrlProvider: props.fileUrlProvider, placeholder: props.placeholder }) })));
}
function createDropdown(cfg) {
    const menuWidth = cfg.anchor.getBoundingClientRect().width;
    const menuKey = String(Math.random());
    cfg.menuControls.showMenu({
        anchor: cfg.anchor,
        render: () => (jsx(DropdownMenu, { optionProps: cfg.options.map(opt => ({
                option: opt,
                onClick: () => {
                    cfg.onChange(opt.id);
                    cfg.menuControls.dismissMenu();
                },
                fileUrlProvider: cfg.fileUrlProvider,
            })), menuWidth: menuWidth }, menuKey)),
        onDismissed: cfg.onMenuDismissed
    });
}
function DropdownMenu(props) {
    const [focusedIdx, setFocusedIdx] = useState(undefined);
    const menuRef = useRef(undefined);
    useAutoFocus(menuRef.current);
    useEffect(() => {
        const keyboardListener = (e) => {
            var _a, _b;
            const changeFocusedIdx = (changeBy) => {
                const nextIdx = focusedIdx != undefined
                    ? Math.max(0, Math.min(props.optionProps.length - 1, focusedIdx + changeBy))
                    : (changeBy == 1 ? 0 : (props.optionProps.length - 1));
                setFocusedIdx(nextIdx);
                scrollContainerToChild(menuRef.current.parentElement, nextIdx, () => menuRef.current.children);
                e.stopPropagation();
                e.preventDefault();
            };
            if (e.key == "ArrowDown") {
                changeFocusedIdx(1);
            }
            else if (e.key == "ArrowUp") {
                changeFocusedIdx(-1);
            }
            else if (e.key == "Enter") {
                if (focusedIdx != undefined) {
                    (_b = (_a = props.optionProps[focusedIdx]) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a);
                    e.stopPropagation();
                    e.preventDefault();
                }
            }
        };
        window.addEventListener("keydown", keyboardListener);
        return () => window.removeEventListener("keydown", keyboardListener);
    }, [props.optionProps, focusedIdx, menuRef]);
    const dropdownOptions = props.optionProps.map((optProps, idx) => {
        var _a;
        return (createElement(DropdownElement, Object.assign({}, optProps, { key: String((_a = optProps.option) === null || _a === void 0 ? void 0 : _a.id), onMouseEnter: () => setFocusedIdx(idx), focused: focusedIdx == idx })));
    });
    return (jsx("div", { tabIndex: -1, ref: menuRef, className: "cdc-dropdown-menu-container", onMouseLeave: () => setFocusedIdx(undefined), style: { width: props.menuWidth }, children: dropdownOptions }));
}
function DropdownElement(props) {
    const option = props.option;
    const media = (option === null || option === void 0 ? void 0 : option.media) && (jsx(MediaDisplay, { tabIndex: -1, media: option.media.file, fileUrlProvider: props.fileUrlProvider }));
    const dropdownArrow = props.showDropdownArrow && (jsx(Icon, { className: "dropdown-arrow", iconName: IconName.DROPDOWN_ARROW }));
    const clearButton = props.onClear != undefined ? (jsx(Icon, Object.assign({}, onActivate(props.onClear), { className: "dropdown-clear", iconName: IconName.CLEAR }))) : null;
    return (jsxs("div", { className: "cdc-dropdown-element " + (props.focused ? " focused" : ""), onMouseEnter: props.onMouseEnter, onClick: props.onClick, children: [media, jsx("div", { className: "label", children: jsx(RichText, { text: (option === null || option === void 0 ? void 0 : option.label) || props.placeholder || "" }) }), dropdownArrow, clearButton] }, String(option === null || option === void 0 ? void 0 : option.id) || "--cdc-placeholder"));
}

function MediaGallery(props) {
    const galleryRef = useRef(null);
    function createScrollButtonOnClick(direction) {
        return function () {
            var _a;
            const target = (_a = galleryRef.current) === null || _a === void 0 ? void 0 : _a.getElementsByClassName("gallery-content")[0];
            if (target) {
                smoothScrollTo(target, target.scrollLeft + target.clientWidth * direction, 200, "x");
            }
        };
    }
    function handleGalleryScroll() {
        var _a;
        const gallery = (_a = galleryRef.current) === null || _a === void 0 ? void 0 : _a.children[0];
        if (gallery) {
            const galleryContent = gallery.getElementsByClassName("gallery-content")[0];
            const scrollBtnLeft = gallery.getElementsByClassName("scroll-control left")[0];
            const scrollBtnRight = gallery.getElementsByClassName("scroll-control right")[0];
            if (galleryContent && scrollBtnLeft && scrollBtnRight) {
                const isLeft = galleryContent.scrollLeft == 0;
                const isRight = galleryContent.scrollWidth == (galleryContent.clientWidth + galleryContent.scrollLeft);
                scrollBtnLeft.style.opacity = String(isLeft ? 0 : 1);
                scrollBtnRight.style.opacity = String(isRight ? 0 : 1);
            }
        }
    }
    useEffect(handleGalleryScroll, [galleryRef]);
    const media = props.media.map((fd, idx) => {
        const activate = (e) => {
            if (props.onClickMedia) {
                props.onClickMedia(e, fd);
            }
        };
        return !fd.file ? null : (jsx(MediaDisplay, Object.assign({ media: fd.file, fileUrlProvider: props.fileUrlProvider, caption: fd.caption }, onActivate(activate)), String(idx)));
    });
    const scrollControls = (props.showScrollControls !== false) ? [
        jsx("div", Object.assign({}, onActivate(createScrollButtonOnClick(-1)), { className: "scroll-control left", children: jsx(Icon, { iconName: IconName.CHEVRON_LEFT }) }), "l"),
        jsx("div", Object.assign({}, onActivate(createScrollButtonOnClick(1)), { className: "scroll-control right", children: jsx(Icon, { iconName: IconName.CHEVRON_RIGHT }) }), "r")
    ] : null;
    return (jsxs("div", { onScroll: handleGalleryScroll, ref: galleryRef, style: props.style, className: "cdc-media-gallery " + (props.className || ""), children: [jsx("div", { className: "gallery-content", children: media }), scrollControls] }));
}

function useMenu() {
    const [menuConfig, setMenuConfig] = useState({ anchor: document.body, render: () => jsx("div", {}), });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const menuRef = useRef(null);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const containerRef = useRef(null);
    const [measuredDimensions, setMeasuredDimensions] = useState({ width: 0, height: 0 });
    /* eslint-disable react-hooks/exhaustive-deps */
    useLayoutEffect(() => {
        var _a, _b;
        if (menuConfig.shown && (menuRef === null || menuRef === void 0 ? void 0 : menuRef.current) && menuRef.current.children.length > 0) {
            const menuContentElement = menuRef.current.children[0];
            const newDimensions = {
                width: Math.min(menuContentElement.offsetWidth, ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) - 20),
                height: Math.min(menuContentElement.offsetHeight, ((_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.offsetHeight) - 20)
            };
            if (!dimensionsEqual(measuredDimensions, newDimensions)) {
                setMeasuredDimensions(newDimensions);
            }
        }
    }, [menuConfig.shown]);
    /* eslint-enable react-hooks/exhaustive-deps */
    const pos = menuConfig && containerRef.current && computeMenuPosition();
    const isShown = menuConfig.shown && pos != undefined;
    function showMenu(cfg) {
        setMenuConfig(Object.assign(Object.assign({}, cfg), { shown: true }));
    }
    const dismissMenu = useCallback(() => {
        setMenuConfig(cfg => {
            var _a;
            (_a = cfg.onDismissed) === null || _a === void 0 ? void 0 : _a.call(cfg);
            return Object.assign(Object.assign({}, cfg), { shown: false });
        });
    }, []);
    // Add keyboard listener
    useEffect(() => {
        const keyboardListener = (e) => {
            if (["Escape", "Tab"].includes(e.key) && isShown) {
                dismissMenu();
                e.preventDefault();
                e.stopPropagation();
            }
        };
        window.addEventListener("keydown", keyboardListener);
        return () => window.removeEventListener("keydown", keyboardListener);
    }, [isShown, dismissMenu]);
    function computeMenuPosition() {
        const requestedPosition = menuConfig.anchor.getBoundingClientRect();
        // Shift coordinates relative to parent container
        requestedPosition.x -= containerRef.current.getBoundingClientRect().x;
        requestedPosition.y -= containerRef.current.getBoundingClientRect().y;
        const maxWidth = containerRef.current.clientWidth;
        const maxHeight = containerRef.current.clientHeight;
        let xOrigin = "left", yOrigin = "left";
        const pos = {};
        // Horizontal placement
        if (requestedPosition.x + measuredDimensions.width < maxWidth) { // Fits right
            pos.left = requestedPosition.x;
            xOrigin = "left";
        }
        else if (requestedPosition.x > measuredDimensions.width) { // Fits left
            pos.right = maxWidth - requestedPosition.x;
            xOrigin = "right";
        }
        else { // Only fits centered
            pos.right = (maxWidth - measuredDimensions.width) / 2;
            xOrigin = "center";
        }
        // Vertical placement
        const topDistance = requestedPosition.y - Math.max(0, requestedPosition.y + measuredDimensions.height - maxHeight + 20);
        pos.top = Math.max(topDistance, 10);
        yOrigin = "top";
        pos.transformOrigin = xOrigin + " " + yOrigin;
        return pos;
    }
    const menuContainer = (jsx("div", { ref: containerRef, className: "cdc-menu-container " + (isShown ? "shown" : ""), onClick: dismissMenu, children: jsx("div", { onClick: e => {
                // Prevent clicks from leaving the menu
                // Exception: content nested in anchor tags inside the menu
                let target = e.target;
                while (target && target != e.currentTarget) {
                    if (target.tagName.toLowerCase() == "a") {
                        return;
                    }
                    target = target.parentElement;
                }
                e.stopPropagation();
                e.preventDefault();
            }, ref: menuRef, "aria-hidden": !isShown, role: isShown ? "alert" : "none", style: Object.assign(Object.assign({}, pos), { height: isShown ? measuredDimensions === null || measuredDimensions === void 0 ? void 0 : measuredDimensions.height : 0 }), className: "cdc-menu " + (isShown ? " shown " : ""), children: menuConfig === null || menuConfig === void 0 ? void 0 : menuConfig.render() }) }));
    return [menuContainer, { showMenu, dismissMenu }];
}
function dimensionsEqual(a, b) {
    return a && b && a.width == b.width && a.height == b.height;
}

const TimeUnits = {
    SECOND: 1000,
    MINUTE: 60000,
    HOUR: 60000 * 60,
    DAY: 60000 * 60 * 24,
    WEEK: 60000 * 60 * 24 * 7,
    MONTH: 60000 * 60 * 24 * 30,
};
// TODO translate
const defaultTimeUnitTexts = {
    SECOND: "Sekunden",
    MINUTE: "Minuten",
    HOUR: "Stunden",
    DAY: "Tage",
    WEEK: "Wochen",
    NONE: "Wenige Sekunden",
};
function durationToString(millis, maxPrecision = Object.keys(TimeUnits).length, maxTextPartCount) {
    let text = "";
    let textPartCount = 0;
    if (millis < TimeUnits.SECOND) {
        return defaultTimeUnitTexts.NONE;
    }
    let actualPrecision = 0;
    for (const tu of Object.entries(durationInTimeUnits(millis))) {
        const amount = tu[1];
        if (amount > 0 && textPartCount < maxTextPartCount) {
            actualPrecision++;
            text += (text ? ", " : "") + amount + " " + defaultTimeUnitTexts[tu[0]];
            textPartCount++;
        }
        if (actualPrecision >= maxPrecision) {
            break;
        }
    }
    return text;
}
function durationInTimeUnits(millis) {
    const result = {};
    Object.entries(TimeUnits).sort((a, b) => b[1] - a[1]).forEach(tu => {
        const amount = Math.floor(millis / tu[1]);
        result[tu[0]] = amount;
        millis -= amount * tu[1];
    });
    return result;
}

function ModelNodeSteps(props) {
    const [currentDate, setCurrentDate] = useState(new Date().getTime());
    const dispatchSteps = props.node.dispatchSteps || [];
    // Update current date every second while steps aren't finished
    useEffect(() => {
        if (!props.isFinished) {
            const interval = setInterval(() => {
                setCurrentDate(new Date().getTime());
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [currentDate, props.isFinished]);
    const stepViews = dispatchSteps.map((step, idx) => {
        const isActiveStep = !props.isFinished && (idx === dispatchSteps.length - 1);
        let etaTimer = null;
        let body = null;
        if (isActiveStep) {
            if (step.nextStepDate) {
                const etaMillis = new Date(step.nextStepDate).getTime() - currentDate;
                // TODO translate
                etaTimer = (jsxs("div", { className: "eta-timer-container", children: [jsx("div", { className: "label", children: "Verbleibende Zeit" }), jsxs("div", { className: "eta-timer", children: [jsx("div", { className: "live-indicator" }), jsx("div", { className: "eta", children: durationToString(etaMillis, undefined, 2) })] })] }));
            }
            else {
                etaTimer = (jsx("div", { className: "eta-timer-container", children: jsx("div", { className: "eta-timer", children: jsx("div", { className: "live-indicator" }) }) }));
            }
        }
        if (isActiveStep && step.description) {
            body = (jsx("div", { className: "body", children: jsx(RichText, { text: step.description }) }));
        }
        return (jsxs("div", { className: `step-item ${etaTimer ? "active" : ""}`, children: [jsxs("div", { className: "header", children: [jsxs("div", { className: "main", children: [jsx("div", { className: "date", children: new Date(step.startDate).toLocaleString() }), jsx("div", { className: "label", children: jsx(RichText, { text: step.label || `#${step.idx + 1}` }) })] }), etaTimer] }), body] }, idx));
    });
    return (jsx("div", { className: "cdc-model-node-steps", children: stepViews }));
}

/**
 * Semi-controlled variant of React's "useState". When the "value" prop changes, the state will be updated
 * to the new value. When changing the "cached" value using the Dispatch ("setValue" function), the state
 * will be flagged as "dirty" when changed.
 * @param value The current reference value. Replaces the cached state and resets the "dirty" flag when changed
 */
function useSemiControlledState(value) {
    const previousValue = usePrevious(value);
    // Keep track of the "cached" value, which can be modified like with "useState"
    const [cachedValue, setCachedValue] = useState(value);
    // Remember whether the "cached" state has diverged from the "reference" state (i.e. the value prop)
    const [isDirty, setIsDirty] = useState(false);
    // Reset "dirty" flag and "cached" state when value prop changes
    useEffect(() => {
        if (previousValue != value) {
            setCachedValue(value);
            setIsDirty(false);
        }
    }, [cachedValue, previousValue, value]);
    // Create a dispatch function with added "dirty"-check
    const dispatch = (nextValue) => {
        setCachedValue(nextValue);
        setIsDirty(nextValue != value);
    };
    return [cachedValue, dispatch, isDirty];
}

function useAutoComplete(cfg) {
    const [state, setState] = useState({
        query: "",
        focusedIdx: 0,
        selectedOptionFetchFailed: false,
        isLoading: false,
        isSelectQueued: false
    });
    // Define setter functions
    const setFocusedIdx = useCallback((idx) => {
        var _a;
        if ((_a = state.result) === null || _a === void 0 ? void 0 : _a.options.length) {
            const nextIdx = Math.max(0, Math.min(state.result.options.length - 1, idx));
            if (nextIdx != state.focusedIdx) {
                setState(Object.assign(Object.assign({}, state), { focusedIdx: nextIdx }));
                return true;
            }
        }
        return false;
    }, [state]);
    const setSelectedOption = useCallback((key) => {
        if (state.selectedOptionKey != key) {
            setState(Object.assign(Object.assign({}, state), { selectedOptionKey: key, selectedOption: undefined, selectedOptionFetchFailed: false, query: (cfg.clearQueryOnSelect !== false) ? "" : state.query, isSelectQueued: false }));
        }
    }, [state, cfg]);
    // Handle keyboard events
    useEffect(() => {
        if (cfg.enableKeyboardControls !== false) {
            const keyDownListener = (e) => {
                var _a, _b;
                const onKeyPressHandled = () => {
                    e.stopPropagation();
                    e.preventDefault();
                };
                if ((e.key == "ArrowDown" && setFocusedIdx(state.focusedIdx + 1))
                    || (e.key == "ArrowUp" && setFocusedIdx(state.focusedIdx - 1))) {
                    onKeyPressHandled();
                }
                else if (e.key == "Enter") {
                    // If currently fetching new options, queue this selection
                    if (cfg.enableQueuedSelection !== false && state.query != ((_a = state.result) === null || _a === void 0 ? void 0 : _a.targetQuery)) {
                        setState(Object.assign(Object.assign({}, state), { isSelectQueued: !state.isSelectQueued }));
                        onKeyPressHandled();
                    }
                    else if ((_b = state.result) === null || _b === void 0 ? void 0 : _b.options.length) {
                        setSelectedOption(state.result.options[state.focusedIdx].key);
                        onKeyPressHandled();
                    }
                }
            };
            window.addEventListener("keydown", keyDownListener);
            return () => window.removeEventListener("keydown", keyDownListener);
        }
        else {
            return undefined;
        }
    }, [cfg.enableKeyboardControls, cfg.enableQueuedSelection, setFocusedIdx, setSelectedOption, state]);
    // Disable ESLint, since it doesn't understand that the effect dependency cfg.someFunction is enough (It asks for cfg as a dependency).
    /* eslint-disable react-hooks/exhaustive-deps */
    // Fetch options when query is ready
    useEffect(() => {
        const query = state.fetchingResultForQuery || "";
        const setOptions = (opts) => setState((currentState) => (Object.assign(Object.assign({}, currentState), { result: {
                options: opts,
                targetQuery: query,
                didFail: opts == undefined,
            }, focusedIdx: 0, isLoading: false })));
        cfg.listOptions(query)
            .then(setOptions)
            .catch(() => setOptions([]));
    }, [state.fetchingResultForQuery, cfg.listOptions]);
    // Fetch selected option data
    useEffect(() => {
        if (state.selectedOptionKey != null) {
            cfg.getOptionForKey(state.selectedOptionKey)
                .then(opt => setState(s => (Object.assign(Object.assign({}, s), { selectedOption: opt, selectedOptionFetchFailed: false }))))
                .catch(() => setState(s => (Object.assign(Object.assign({}, s), { selectedOption: undefined, selectedOptionFetchFailed: true }))));
        }
        else {
            setState(Object.assign(Object.assign({}, state), { selectedOption: undefined, selectedOptionFetchFailed: false }));
        }
    }, [state.selectedOptionKey, cfg.getOptionForKey]);
    /* eslint-enable react-hooks/exhaustive-deps */
    // Handle query change timeout
    useEffect(() => {
        const ref = setTimeout(() => {
            // Fetch options for query
            setState((currentState) => (Object.assign(Object.assign({}, currentState), { fetchingResultForQuery: state.query, isLoading: true })));
        }, cfg.queryUpdateDelay || 350);
        return () => clearTimeout(ref);
    }, [state.query, cfg.queryUpdateDelay]);
    // Handle selection of queued option
    useEffect(() => {
        if (state.result && state.isSelectQueued) {
            const selectedOption = state.result.options[state.focusedIdx];
            if (selectedOption) {
                setSelectedOption(selectedOption.key);
            }
        }
    }, [state.result, state.isSelectQueued, state.selectedOption, setSelectedOption, state.focusedIdx]);
    const updateAutoComplete = useMemo(() => ({
        setQuery: (query) => {
            setState(s => (Object.assign(Object.assign({}, s), { query: query })));
        },
        setFocusedIdx: setFocusedIdx,
        setSelectedOption: setSelectedOption
    }), [setFocusedIdx, setSelectedOption]);
    return [
        state,
        updateAutoComplete
    ];
}

/**
 * DateInputComponent is an uncontrolled component
 * @param props.allowNativeDatePicker: When mobile browser is detected, use the browser's native date picker
 * @param props.minDateErrorText: Text to show when min date constraint is not met. Use $date or $0 as the placeholder. E.g. "Min. date is $date"
 * @param props.maxDateErrorText: Equivalent to minDateErrorText param
 */
function DateInputComponent(props) {
    const inputRef = useRef(null);
    const { value, setValue, useNativeDatePicker, errorMessage, datePicker } = useDateInputSupport(props);
    const componentProps = {
        className: "cdc-date-input " + (props.className || ""),
        style: props.style
    };
    const onEditIconClick = () => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    };
    const editIcon = props.editIcon ? (jsx(props.editIcon, { onClick: useNativeDatePicker ? undefined : onEditIconClick })) : (jsx(Icon, { iconName: IconName.EDIT, onClick: useNativeDatePicker ? undefined : onEditIconClick }));
    const errorView = errorMessage ? (jsxs("div", { className: "error-text", children: [jsx(Icon, { iconName: IconName.CLEAR }), errorMessage] })) : null;
    if (useNativeDatePicker) {
        return (jsxs("div", Object.assign({}, componentProps, { children: [jsxs("div", { className: "date-field-container", children: [editIcon, jsx("input", { type: "date", value: value ? DateInputUtils.formatISODateStringForHTMLDateInput(value) : "", onChange: e => setValue(e.target.value), className: "date-field" })] }), errorView] })));
    }
    else {
        return (jsxs("div", Object.assign({}, componentProps, { children: [jsx("div", { "aria-hidden": true, children: datePicker }), jsx("div", { className: "separator" }), jsxs("div", { className: "date-field-container", children: [editIcon, jsx("input", { tabIndex: 0, autoFocus: props.autoFocus, value: value || "", onChange: e => setValue(e.target.value), className: "date-field", ref: inputRef })] }), errorView] })));
    }
}
function useDateInputSupport(props) {
    const inputField = props.inputField;
    const dateConstraints = DateInputUtils.getDateConstraints(inputField) || { minDate: undefined, maxDate: undefined };
    const useNativeDatePicker = (props.allowNativeDatePicker != false) && isMobileBrowser();
    const defaultInputText = useNativeDatePicker
        ? (props.defaultValue ? DateInputUtils.formatISODateStringForHTMLDateInput(props.defaultValue) : "")
        : (props.defaultValue ? DateInputUtils.formatISODateString(inputField, props.defaultValue) : "");
    const [state, setState] = useState({
        inputText: defaultInputText,
        dateFormatValid: !!props.defaultValue,
        value: props.defaultValue,
        selectedMonth: getInitialMonthToShow(props.defaultValue, dateConstraints)
    });
    const inputValidation = DateInputUtils.validateInput(inputField, state.inputText);
    let errorMessage = null;
    const errorRegexp = /(\$date)|(\$0)/g;
    if (!state.dateFormatValid && state.inputText.length) {
        errorMessage = props.invalidDateFormatErrorText;
    }
    else if (inputValidation.lowerLimitExceeded && dateConstraints.minDate) {
        errorMessage = (props.minDateErrorText || "Min. $date")
            .replace(errorRegexp, DateInputUtils.formatDate(inputField, dateConstraints.minDate));
    }
    else if (inputValidation.upperLimitExceeded && dateConstraints.maxDate) {
        errorMessage = (props.maxDateErrorText || "Max. $date")
            .replace(errorRegexp, DateInputUtils.formatDate(inputField, dateConstraints.maxDate));
    }
    function handlePickDate(date) {
        const dateAsISOString = date.toISOString();
        setState({
            inputText: DateInputUtils.formatISODateString(inputField, dateAsISOString),
            dateFormatValid: true,
            value: dateAsISOString,
            selectedMonth: new Date(dateAsISOString)
        });
        props.onDateChange(dateAsISOString);
    }
    function handleInputTextUpdate(text) {
        const date = DateInputUtils.parseDate(inputField, text);
        const validation = DateInputUtils.validateDate(inputField, date);
        let nextValue = state.value;
        let selectedMonth = state.selectedMonth;
        if (validation.valid) {
            nextValue = date;
            selectedMonth = nextValue ? new Date(nextValue) : state.selectedMonth; // TODO is this correct?
            props.onDateChange(date);
        }
        else {
            props.onDateChange(undefined);
        }
        setState({
            inputText: text,
            value: nextValue,
            selectedMonth: selectedMonth,
            dateFormatValid: !!date
        });
    }
    return {
        value: state.inputText,
        setValue: handleInputTextUpdate,
        useNativeDatePicker,
        errorMessage: errorMessage || null,
        datePicker: (jsx(DayPicker
        // months={ this.props.config.messages.monthNames }
        // TODO I18n
        , { 
            // months={ this.props.config.messages.monthNames }
            // TODO I18n
            mode: "single", fromMonth: dateConstraints.minDate ? new Date(dateConstraints.minDate) : undefined, toMonth: dateConstraints.maxDate ? new Date(dateConstraints.maxDate) : undefined, month: state.selectedMonth, onMonthChange: (month) => setState({
                value: state.value,
                inputText: state.inputText,
                selectedMonth: month,
                dateFormatValid: true
            }), disabled: (date) => {
                date.setHours(12);
                return !DateInputUtils.validateDate(inputField, date.toISOString()).valid;
            }, onDayClick: (date, modifiers) => {
                if (!modifiers.disabled) {
                    date.setHours(12);
                    handlePickDate(date);
                }
            }, selected: state.value ? new Date(state.value) : undefined }))
    };
}
function getInitialMonthToShow(isoStringDate, dateConstraints) {
    let initialMonth = (isoStringDate ? new Date(isoStringDate) : undefined) || new Date();
    if (dateConstraints.minDate && initialMonth < new Date(dateConstraints.minDate)) {
        initialMonth = new Date(dateConstraints.minDate);
    }
    else if (dateConstraints.maxDate && initialMonth > new Date(dateConstraints.maxDate)) {
        initialMonth = new Date(dateConstraints.maxDate);
    }
    return initialMonth;
}

function useInputModule(cfg) {
    var _a, _b, _c, _d, _e;
    const [files, setFiles] = useState({});
    const [inputMap, setInputMap] = useState({});
    const [lastUpdatedInput, setLastUpdatedInput] = useState(null);
    const [inputMapTargetIdx, setInputMapTargetIdx] = useState(((_a = cfg.history) === null || _a === void 0 ? void 0 : _a.path.length) || 0);
    const inputMapFromHistory = ((_c = (_b = cfg.history) === null || _b === void 0 ? void 0 : _b.path) === null || _c === void 0 ? void 0 : _c.length)
        ? Object.assign({}, cfg.history.path[cfg.history.path.length - 1].inputMap)
        : {};
    const proposedInputMap = cfg.proposedInputMap || {};
    const isInputMapOutdated = ((_d = cfg.history) === null || _d === void 0 ? void 0 : _d.path.length) != inputMapTargetIdx;
    const mergedInputMap = Object.assign({}, proposedInputMap, inputMapFromHistory, isInputMapOutdated ? {} : inputMap);
    // Clear input map when history changes
    useEffect(() => {
        if (cfg.history && cfg.history.path.length != inputMapTargetIdx) {
            setInputMapTargetIdx(cfg.history.path.length);
            setInputMap({});
        }
    }, [cfg.history, inputMapTargetIdx]);
    // Notify when input map changes
    useEffect(() => {
        var _a;
        if (lastUpdatedInput) {
            // Notify the parent component
            (_a = cfg.onComponentChange) === null || _a === void 0 ? void 0 : _a.call(cfg, lastUpdatedInput.input, lastUpdatedInput.isConfirmed);
            // Consume the update
            setLastUpdatedInput(null);
        }
    }, [lastUpdatedInput, cfg]);
    // Clear file map after submit
    useEffect(() => {
        var _a;
        if (((_a = cfg.history) === null || _a === void 0 ? void 0 : _a.path.length) != inputMapTargetIdx) {
            setFiles({});
        }
    }, [(_e = cfg.history) === null || _e === void 0 ? void 0 : _e.path.length, inputMapTargetIdx]);
    function renderInputFields(fields, autoFocus) {
        const renderedInputFields = {};
        fields.forEach((field, idx) => {
            var _a, _b;
            const valueType = field.valueType;
            const Component = cfg.components[valueType];
            const fieldId = String(field.id);
            const suppliedProps = ((_a = cfg.componentPropSupplier) === null || _a === void 0 ? void 0 : _a.call(cfg, field.id)) || {};
            const maybeNodeInput = (mergedInputMap[fieldId] && !isEmptyInput(mergedInputMap[fieldId])) ?
                mergedInputMap[fieldId] : undefined;
            const storeFile = (file) => {
                if (file)
                    setFiles(existing => (Object.assign(Object.assign({}, existing), { [fieldId]: file })));
                else
                    setFiles(existing => {
                        delete existing[fieldId];
                        return existing;
                    });
            };
            renderedInputFields[fieldId] = (createElement(Component, Object.assign({}, suppliedProps, { key: `${fieldId}/${(_b = cfg.history) === null || _b === void 0 ? void 0 : _b.path.length}`, inputField: field, value: maybeNodeInput, onChange: (updated, isConfirmed) => {
                    setLastUpdatedInput({
                        nodeId: field.id,
                        input: updated,
                        isConfirmed: isConfirmed !== false
                    });
                    setInputMap(Object.assign(Object.assign({}, inputMap), { [fieldId]: updated }));
                }, storeFile: storeFile, autoFocus: (!idx && cfg.autoFocusInput) || (autoFocus === null || autoFocus === void 0 ? void 0 : autoFocus[fieldId]) || false })));
        });
        return renderedInputFields;
    }
    return [mergedInputMap, files, setInputMap, renderInputFields];
}

/** React hook wrapper of ReaderModule */
function useReaderModule(createReaderModule) {
    var _a, _b;
    const [state, setState] = useState(() => ({
        isLoading: true,
        readerModule: createReaderModule(),
        unmodifiedHistoryIdx: null
    }));
    useEffect(() => {
        if (state.readerModule) {
            // ReaderModule is already initialized, only update event handlers
            state.readerModule.setEventHandlers({
                onRunUpdated: run => {
                    var _a, _b, _c;
                    // Check whether unmodifiedHistoryIdx should be updated
                    const maybeNextUnmodifiedIdx = ((_c = (_b = (_a = run === null || run === void 0 ? void 0 : run.state) === null || _a === void 0 ? void 0 : _a.history) === null || _b === void 0 ? void 0 : _b.path) === null || _c === void 0 ? void 0 : _c.length) != null ? run.state.history.path.length - 1 : null;
                    const nextUnmodifiedIdx = state.unmodifiedHistoryIdx == null ? maybeNextUnmodifiedIdx : (maybeNextUnmodifiedIdx != null && maybeNextUnmodifiedIdx < state.unmodifiedHistoryIdx
                        ? maybeNextUnmodifiedIdx
                        : state.unmodifiedHistoryIdx);
                    setState(s => (Object.assign(Object.assign({}, s), { run: run, isLoading: false, unmodifiedHistoryIdx: nextUnmodifiedIdx })));
                },
                onError: err => {
                    Sentry.captureException(err);
                    setState(s => (Object.assign(Object.assign({}, s), { runError: err, isLoading: false })));
                },
                onApiRequest: () => setState(s => (Object.assign(Object.assign({}, s), { isLoading: true })))
            });
        }
    }, [state]);
    const hasApiRoutes = !!((_a = state.readerModule) === null || _a === void 0 ? void 0 : _a.getApiRoutes());
    return Object.assign(Object.assign({}, state), { getFileUrl: useCallback(fileId => {
            var _a, _b;
            const apiRoutes = (_a = state.readerModule) === null || _a === void 0 ? void 0 : _a.getApiRoutes();
            if (!apiRoutes) {
                throw new Error("No reader module or api routes available to get file view token");
            }
            const fileViewToken = (_b = state.run) === null || _b === void 0 ? void 0 : _b.fileViewToken;
            if (!fileViewToken) {
                throw new Error("No file view token available");
            }
            return apiRoutes.Files.getFileDownloadUrl(fileId, fileViewToken);
        }, [(_b = state.run) === null || _b === void 0 ? void 0 : _b.fileViewToken, hasApiRoutes, state.readerModule]) });
}

export { ActivationMode, DateInputComponent, Dropdown, IMAGE_EXTENSIONS, Icon, IconName, MediaDisplay, MediaGallery, ModelNodeSteps, RichText, createDropdown, durationToString, onActivate, scrollContainerToChild, useAutoComplete, useAutoFocus, useDateInputSupport, useInputModule, useMenu, usePrevious, useReaderModule, useSemiControlledState };
